import React, { useEffect, useContext, useState } from "react";
import { useLocation, useHistory, useParams } from "react-router-dom";
import ReactPlayer from "react-player";
import dayjs from "dayjs";
import { Helmet } from "react-helmet";

import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import EventNoteIcon from "@mui/icons-material/EventNote";
import { Typography } from "@mui/material";
import { Box } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import BarChartIcon from "@mui/icons-material/BarChart";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import LinearProgress from "@mui/material/LinearProgress";
import Skeleton from "@mui/material/Skeleton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DataUsageOutlinedIcon from "@mui/icons-material/DataUsageOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import CloudSyncOutlinedIcon from "@mui/icons-material/CloudSyncOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

// My Files
import { DatabaseContext, UserContext } from "../../../../context";
import ToolbarWithLogo from "../../../ToolbarWithLogo";
import CommentSection from "../CommentSection";
import firebase from "../../../../firebase";
import { deleteFileRecordAndNotifications } from "../fileDeletion";
import NoPermissionsMessage from "../../../NoPermissionsMessage";
import FileEventsDialog from "../FileEventsDialog";
import EditFileDialog from "../EditFileDialog";
import FileUsageDialog from "../FileUsageDialog";
import {
    shouldFileBeVisible,
    doesFileHaveDuplicate,
} from "../../../../utility";
import VideoPlaylistsAndTimelinesDialog from "../../../playlists/VideoPlaylistsAndTimelinesDialog";
import ShareLinkDialog from "../ShareLinkDialog";
import DeleteFileDialog from "../DeleteFileDialog";
import { useVideoFileDownload } from "../useFileDownloads";
import useBunnyVideoReadiness, {
    getBunnyPlaylistUrl,
} from "../useBunnyVideoReadiness";
import { deletePlaylistOrTimelineRecord } from "../../../playlists/playlistTimelineDeletion";

// let secondsViewed = 0;
// let usageKey = makeid(19);
let lastProgress = 0.0;

function VideoProcessingState({ readiness }) {
    const hasProgress = Number.isFinite(readiness.progress);
    const title = readiness.isTakingLonger
        ? "This video is still being prepared"
        : "Your video is being prepared";

    return (
        <Box
            role="status"
            aria-live="polite"
            sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                px: { xs: 2.5, sm: 5 },
                textAlign: "center",
                color: "white",
                background:
                    "radial-gradient(circle at 50% 35%, rgba(0, 149, 61, 0.2), transparent 46%), linear-gradient(145deg, #111b15 0%, #19261d 100%)",
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    width: 58,
                    height: 58,
                    display: "grid",
                    placeItems: "center",
                    mb: 2,
                }}
            >
                <CircularProgress
                    size={58}
                    thickness={2.4}
                    sx={{
                        position: "absolute",
                        color: "rgba(87, 219, 139, 0.72)",
                    }}
                />
                <CloudSyncOutlinedIcon sx={{ fontSize: 27 }} />
            </Box>
            <Typography
                variant="h5"
                component="h2"
                sx={{ color: "white", lineHeight: 1.15 }}
            >
                {title}
            </Typography>
            <Typography
                variant="body2"
                sx={{
                    mt: 1,
                    maxWidth: 520,
                    color: "rgba(255, 255, 255, 0.74)",
                }}
            >
                {readiness.isTakingLonger
                    ? "It is taking a little longer than usual to process this video. You can leave this page and return later."
                    : "The upload is complete. The video is processing. This page will update automatically."}
            </Typography>

            {hasProgress && readiness.progress < 100 && (
                <Box sx={{ width: "min(360px, 82%)", mt: 2.5 }}>
                    <LinearProgress
                        variant="determinate"
                        value={readiness.progress}
                        sx={{
                            height: 7,
                            borderRadius: 5,
                            backgroundColor: "rgba(255, 255, 255, 0.15)",
                            "& .MuiLinearProgress-bar": {
                                backgroundColor: "rgb(87, 219, 139)",
                            },
                        }}
                    />
                    <Typography
                        variant="caption"
                        sx={{
                            display: "block",
                            mt: 0.75,
                            color: "rgba(255, 255, 255, 0.7)",
                        }}
                    >
                        Encoding {readiness.progress}%
                    </Typography>
                </Box>
            )}

            <Button
                color="inherit"
                size="small"
                startIcon={<RefreshOutlinedIcon />}
                onClick={readiness.retry}
                sx={{ mt: 2, color: "rgba(255, 255, 255, 0.88)" }}
            >
                Check again
            </Button>
        </Box>
    );
}

export default function VideoViewPage() {
    const history = useHistory();
    const location = useLocation();
    const { videoKey } = useParams();
    const downloadVideo = useVideoFileDownload();

    const {
        files,
        folders,
        comments,
        events,
        users,
        groups,
        timelineInfo,
        playlistInfo,
    } = useContext(DatabaseContext);

    const [folder, setFolder] = useState();
    const [file, setFile] = useState();
    const playerRef = React.useRef();
    const videoReadiness = useBunnyVideoReadiness(file);
    const [playerLoadFailed, setPlayerLoadFailed] = useState(false);

    const { currentUserData } = useContext(UserContext);
    const [anchorEl, setAnchorEl] = useState(null);

    const [isPlaying, setIsPlaying] = useState(false);

    const [userHasPermission, setUserHasPermission] = useState();

    const [userCanUseAnalysis, setUserCanUseAnalysis] = useState(false);
    const [userCanUsePlaylists, setUserCanUsePlaylists] = useState(false);

    const [userIsManager, setUserIsManager] = useState(false);
    const userCanSharePublicLink =
        userIsManager ||
        (file?.allowSharing === true &&
            currentUserData?.permissions?.shareFiles === true);

    const [confirmDeleteDialogIsOpen, setConfirmDeleteDialogIsOpen] =
        useState(false);

    const [eventDialogOpen, setEventDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [usageDialogOpen, setUsageDialogOpen] = useState(false);
    const [shareLinkDialogOpen, setShareLinkDialogOpen] = useState(false);

    const [analysisSelectDialogOpen, setAnalysisSelectDialogOpen] =
        useState(false);

    // const [usageKey, setUsageKey] = useState(makeid(19)); // The entry in the database used for recording file usage
    // const [secondsViewed, setSecondsViewed] = useState(0);

    const [deleteInProgress, setDeleteInProgress] = useState(false);

    const [uploadedBy, setUploadedBy] = useState("");

    const [hasLoggedUsage, setHasLoggedUsage] = useState(false);

    const [isInFavourites, setIsInFavourites] = useState(false);

    useEffect(() => {
        setPlayerLoadFailed(false);
    }, [file?.key]);

    // Download progress lives in refs so values survive re-renders
    useEffect(() => {
        const handleSpacebar = (event) => {
            if (
                event.repeat ||
                (event.code !== "Space" && event.key !== " ") ||
                !file ||
                !userHasPermission
            ) {
                return;
            }

            const target = event.target;
            const isEditable =
                target instanceof HTMLElement &&
                (target.tagName === "INPUT" ||
                    target.tagName === "TEXTAREA" ||
                    target.tagName === "SELECT" ||
                    target.isContentEditable ||
                    Boolean(target.closest("[contenteditable='true']")));

            if (isEditable) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            setIsPlaying((playing) => !playing);
        };

        document.addEventListener("keydown", handleSpacebar, true);

        return () => {
            document.removeEventListener("keydown", handleSpacebar, true);
        };
    }, [file, userHasPermission]);

    useEffect(() => {
        const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
            // detaching the listener
            if (!user) {
                // If no user is logged in, redirect to sign in page
                // history.replace("/signin");
                history.replace("/signin?url=" + location.pathname);
            }
        });

        // usageKey = makeid(19);
        // secondsViewed = 0;
        lastProgress = 0;

        if (currentUserData) {
            if (currentUserData.permissions.createOwnPlaylist === true) {
                setUserCanUsePlaylists(true);
            } else {
                setUserCanUsePlaylists(false);
            }

            if (currentUserData.permissions.analysis === true) {
                setUserCanUseAnalysis(true);
            } else {
                setUserCanUseAnalysis(false);
            }

            // let currentFileKey = location.pathname.replace("/video/", "");

            let file = files.find((f) => f.key === videoKey);

            if (file) {
                setFile(file);

                let folder = folders.find((f) => f.key === file.folder);

                if (folder) {
                    setFolder(folder);
                }

                if (shouldFileBeVisible(file, groups, currentUserData)) {
                    setUserHasPermission(true);
                } else {
                    setUserHasPermission(false);
                }

                if (currentUserData.faveFiles) {
                    if (currentUserData.faveFiles.includes(file.key)) {
                        setIsInFavourites(true);
                    } else {
                        setIsInFavourites(false);
                    }
                } else {
                    setIsInFavourites(false);
                }

                setUserIsManager(
                    file.managers.includes(currentUserData.key) ||
                        currentUserData.role === "System Digital Manager" ||
                        file.uploadedBy === currentUserData.key,
                );

                if (file.uploadedBy) {
                    let uploadedByUser = users.find(
                        (u) => u.key === file.uploadedBy,
                    );
                    if (uploadedByUser) {
                        setUploadedBy(
                            uploadedByUser.firstName +
                                " " +
                                uploadedByUser.lastName,
                        );
                    }
                }

                // Record File Access
                if (!hasLoggedUsage) {
                    console.log("Log File Usage");

                    let ref = firebase.database().ref("fileusage");

                    ref.push({
                        file: file.key,
                        fileName: file.displayName,
                        fileType: file.type,
                        user: currentUserData.key,
                        action: "Open",
                        date: dayjs().toString(),
                    });

                    setHasLoggedUsage(true);
                }
            }
        }

        return () => unsubscribe();
        // eslint-disable-next-line
    }, [files, file, location.pathname, history, currentUserData, videoKey]);

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    function handleDeleteClick() {
        setAnchorEl(null);
        setConfirmDeleteDialogIsOpen(true);
    }

    function handleConfirmDeleteDialogClose() {
        setConfirmDeleteDialogIsOpen(false);
    }

    function handleEventDialogOpen() {
        setEventDialogOpen(true);
    }

    function handleCloseEventDialog() {
        setEventDialogOpen(false);
    }

    function handleEditClick() {
        setAnchorEl(null);
        setEditDialogOpen(true);
    }

    function handleUsageClick() {
        setAnchorEl(null);
        setUsageDialogOpen(true);
    }

    async function handleConfirmDeletePressed() {
        if (userIsManager) {
            setDeleteInProgress(true);

            // console.clear();
            // console.log("Timelines: " + timelines.length);
            // console.log("Playlists: " + playlists.length);
            if (doesFileHaveDuplicate(file, files)) {
                await deleteFromFirebase();
            } else {
                await deleteFromBunnyStorage();
                await deleteFromFirebase();
            }
        }

        setConfirmDeleteDialogIsOpen(false);
    }

    async function deleteFromFirebase() {
        // Delete the comments
        comments.forEach((comment) => {
            if (comment.file === file.key) {
                let commentRef = firebase
                    .database()
                    .ref("comments")
                    .child(comment.key);
                commentRef.update({ deleted: true });
            }
        });
        // Delete the file in any events
        events.forEach((event) => {
            let filesToKeepArray = [];
            let needsChange = false;
            event.files.forEach((aFile) => {
                if (aFile.key === file.key) {
                    needsChange = true;
                } else {
                    filesToKeepArray.push(aFile.key);
                }
            });
            if (needsChange) {
                let eventRef = firebase
                    .database()
                    .ref("events")
                    .child(event.key);
                // Change remaining files to JSON
                let filesObject = {};
                filesToKeepArray.forEach((aFile) => {
                    filesObject[aFile] = true;
                });
                eventRef.update({ files: filesObject });
            }
        });

        const associatedItemDeletions = [
            ...playlistInfo
                .filter((playlist) => playlist.video === file.key)
                .map((playlist) =>
                    deletePlaylistOrTimelineRecord("playlist", playlist.key),
                ),
            ...timelineInfo
                .filter((timeline) => timeline.video === file.key)
                .map((timeline) =>
                    deletePlaylistOrTimelineRecord("timeline", timeline.key),
                ),
        ];

        await Promise.all(associatedItemDeletions);

        // Delete the file from any users (they are added here if the user is added manually to a file permissions when uploading)
        users.forEach((user) => {
            let filesToKeepArray = [];
            let needsChange = false;
            user.files.forEach((aFile) => {
                if (aFile === file.key) {
                    needsChange = true;
                } else {
                    filesToKeepArray.push(aFile);
                }
            });
            if (needsChange) {
                let userRef = firebase.database().ref("users").child(user.key);
                // Change remaining files to JSON
                let filesObject = {};
                filesToKeepArray.forEach((aFile) => {
                    filesObject[aFile] = true;
                });
                userRef.update({ files: filesObject });
            }
        });

        await deleteFileRecordAndNotifications(file.key);
        setDeleteInProgress(false);

        handleBackToFiles();
    }

    function deleteFromBunnyStorage() {
        let videoKey = file.original ? file.original : file.key;

        const deleteBunnyVideo = firebase
            .functions()
            .httpsCallable("deleteBunnyVideo");

        return deleteBunnyVideo({ videoKey: videoKey });
    }

    function handleDownloadClick() {
        handleMenuClose();
        downloadVideo(file);
    }

    function onProgress(progress) {
        let seconds = progress.playedSeconds;

        let diff = Math.abs(lastProgress - seconds);

        if (diff < 2.1) {
            // Ignore when difference is more than 2 seconds, because the user is probably skipping around the video
            // secondsViewed += diff;
        }

        lastProgress = seconds;
    }

    function handleCloseEditDialog() {
        // EditFileDialog shows its own save confirmation
        setEditDialogOpen(false);
    }

    function handleCloseUsageDialog() {
        setUsageDialogOpen(false);
    }

    async function handleAnalysisButtonPress() {
        setAnalysisSelectDialogOpen(true);
    }

    function handleCloseAnalysisSelectDialog() {
        setAnalysisSelectDialogOpen(false);
    }

    function handleAddToFavouritesPress() {
        // Get the users current favourite files
        let favesObject = {};

        currentUserData.faveFiles.forEach((faveFile) => {
            favesObject[faveFile] = true;
        });

        // Add the current file

        favesObject[file.key] = true;

        // Update firebase

        let ref = firebase.database().ref("users").child(currentUserData.key);
        ref.update({ faveFiles: favesObject });
    }

    function handleRemoveFromFavouritesPress() {
        // Get the users current favourite files
        let favesObject = {};

        currentUserData.faveFiles.forEach((faveFile) => {
            if (faveFile !== file.key) {
                favesObject[faveFile] = true;
            }
        });

        // Update firebase

        let ref = firebase.database().ref("users").child(currentUserData.key);
        ref.update({ faveFiles: favesObject });
    }

    function handleCopyLinkClick() {
        let url = "https://" + window.location.host + "/video/" + file.key;

        navigator.clipboard.writeText(url);

        setAnchorEl(null);
    }

    function handleBackToFiles() {
        const returnTo = location.state?.returnTo;

        if (returnTo && returnTo.startsWith("/files")) {
            history.goBack();
            return;
        }

        if (file?.folder) {
            history.push(`/files/${file.folder}`);
        } else {
            history.push("/files");
        }
    }

    //     <!-- Primary Meta Tags -->
    // <title>Nodiant</title>
    // <meta name="title" content="Nodiant" />
    // <meta name="description" content="Test Video 8" />

    // <!-- Open Graph / Facebook -->
    // <meta property="og:type" content="website" />
    // <meta property="og:url" content="https://nodiant.faw.cymru/" />
    // <meta property="og:title" content="Nodiant" />
    // <meta property="og:description" content="Test Video 8" />
    // <meta property="og:image" content="https://metatags.io/images/meta-tags.png" />

    // <!-- Twitter -->
    // <meta property="twitter:card" content="summary_large_image" />
    // <meta property="twitter:url" content="https://nodiant.faw.cymru/" />
    // <meta property="twitter:title" content="Nodiant" />
    // <meta property="twitter:description" content="Test Video 8" />
    // <meta property="twitter:image" content="https://metatags.io/images/meta-tags.png" />

    // <!-- Meta Tags Generated with https://metatags.io -->

    return (
        <>
            <Helmet>
                {/* <title>Nodiant | Files</title> */}
                {/* <link
                    rel="canonical"
                    href="https://nodiant.faw.cymru"
                /> */}
                {/* <meta name="Nodiant Video" content="Nested component" />
                <meta
                    description={file ? file.displayName : "Nodiant Video Link"}
                    content="Nested component"
                /> */}

                <meta
                    name="description"
                    content={file ? file.displayName : "Nodiant Video Link"}
                />
            </Helmet>
            {/* <SuperSEO
                title={"Nodiant | Files"}
                description={file ? file.displayName : "Nodiant Video Link"}
                lang="en"
                openGraph={{
                    ogTitle: "Nodiant FAW Performance Analysis",
                    ogDescription: "Nodiant Video",
                    ogType: "website",
                    ogUrl: "https://nodiant.faw.cymru",
                    ogImage: {
                        ogImage:
                            "https://vz-db0e7359-d70.b-cdn.net/" +
                            fileKey +
                            "/thumbnail.jpg",
                        ogImageAlt: "Video",
                        ogImageWidth: 1200,
                        ogImageHeight: 630,
                        ogImageType: "image/jpeg",
                    },
                }}
                twitter={{
                    twitterSummaryCard: {
                        summaryCardImage: "http://placekitten.com/1200/630",
                        summaryCardImageAlt: "Kittens",
                        summaryCardSiteUsername: "justinmahar",
                    },
                }}
            /> */}
            {userHasPermission === undefined && (
                <Box
                    sx={{
                        minHeight: {
                            xs: "calc(100dvh - 60px)",
                            sm: "calc(100vh - 128px)",
                        },
                        backgroundColor: "background.default",
                        pt: { xs: 2, md: 3 },
                        pb: 10,
                    }}
                >
                    <Container
                        maxWidth={false}
                        sx={{
                            maxWidth: "1520px",
                            px: { xs: 1.5, sm: 3 },
                        }}
                    >
                        <Skeleton
                            variant="rounded"
                            width={86}
                            height={34}
                            sx={{ mb: 1.5, borderRadius: 2 }}
                        />
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "minmax(0, 1fr)",
                                    md: "minmax(0, 1.8fr) minmax(320px, 0.8fr)",
                                },
                                gap: 2,
                            }}
                        >
                            <Box>
                                <Skeleton
                                    variant="rounded"
                                    width="100%"
                                    sx={{
                                        pt: "56.25%",
                                        borderRadius: 3,
                                    }}
                                />
                                <Skeleton
                                    variant="text"
                                    width="58%"
                                    height={48}
                                    sx={{ mt: 1.5 }}
                                />
                                <Skeleton
                                    variant="text"
                                    width="34%"
                                    height={26}
                                />
                            </Box>
                            <Skeleton
                                variant="rounded"
                                width="100%"
                                height={420}
                                sx={{ borderRadius: 3 }}
                            />
                        </Box>
                    </Container>
                </Box>
            )}
            {userHasPermission && (
                <>
                    <Box
                        sx={{
                            minHeight: {
                                xs: "calc(100dvh - 60px)",
                                sm: "calc(100vh - 128px)",
                            },
                            backgroundColor: "background.default",
                            pt: { xs: 1.5, sm: 2.5, md: 3 },
                            pb: 10,
                        }}
                    >
                        <Container
                            maxWidth={false}
                            sx={{
                                maxWidth: "1520px",
                                px: { xs: 1.5, sm: 3 },
                            }}
                        >
                            <Button
                                color="inherit"
                                startIcon={<ArrowBackIcon />}
                                onClick={handleBackToFiles}
                                sx={{
                                    mb: 1.5,
                                    color: "text.secondary",
                                }}
                            >
                                Back to Files
                            </Button>

                            {file && (
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: {
                                            xs: "minmax(0, 1fr)",
                                            md: "minmax(0, 1.8fr) minmax(320px, 0.8fr)",
                                        },
                                        gap: 2,
                                        alignItems: "start",
                                    }}
                                >
                                    <Box sx={{ minWidth: 0 }}>
                                        <Card
                                            variant="outlined"
                                            sx={{
                                                overflow: "hidden",
                                                borderRadius: 3,
                                                borderColor: "divider",
                                                backgroundColor:
                                                    "background.paper",
                                                boxShadow:
                                                    "0 6px 24px rgba(20, 35, 25, 0.07)",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    position: "relative",
                                                    width: "100%",
                                                    pt: "56.25%",
                                                    backgroundColor: "black",
                                                }}
                                            >
                                                {videoReadiness.isProcessing ||
                                                (videoReadiness.status ===
                                                    "unknown" &&
                                                    playerLoadFailed) ? (
                                                    <VideoProcessingState
                                                        readiness={
                                                            videoReadiness
                                                        }
                                                    />
                                                ) : (
                                                    <ReactPlayer
                                                        ref={playerRef}
                                                        url={getBunnyPlaylistUrl(
                                                            file,
                                                        )}
                                                        autoPlay={false}
                                                        controls
                                                        playing={isPlaying}
                                                        onReady={() =>
                                                            setPlayerLoadFailed(
                                                                false,
                                                            )
                                                        }
                                                        onError={() =>
                                                            setPlayerLoadFailed(
                                                                true,
                                                            )
                                                        }
                                                        onPlay={() =>
                                                            setIsPlaying(true)
                                                        }
                                                        onPause={() =>
                                                            setIsPlaying(false)
                                                        }
                                                        onEnded={() =>
                                                            setIsPlaying(false)
                                                        }
                                                        onProgress={onProgress}
                                                        width="100%"
                                                        height="100%"
                                                        style={{
                                                            position:
                                                                "absolute",
                                                            top: 0,
                                                            left: 0,
                                                        }}
                                                    />
                                                )}
                                            </Box>

                                            <CardContent
                                                sx={{
                                                    p: { xs: 2, sm: 3 },
                                                    "&:last-child": {
                                                        pb: { xs: 2, sm: 3 },
                                                    },
                                                }}
                                            >
                                                <Typography
                                                    variant="h4"
                                                    component="h1"
                                                    sx={{
                                                        overflowWrap:
                                                            "break-word",
                                                        lineHeight: 1.2,
                                                        fontSize: {
                                                            xs: 24,
                                                            sm: 30,
                                                        },
                                                    }}
                                                >
                                                    {file.displayName}
                                                </Typography>

                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        flexWrap: "wrap",
                                                        gap: 1.25,
                                                        mt: 1,
                                                        color: "text.secondary",
                                                    }}
                                                >
                                                    {folder?.name && (
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                gap: 0.5,
                                                            }}
                                                        >
                                                            <FolderOutlinedIcon
                                                                sx={{
                                                                    fontSize: 17,
                                                                }}
                                                            />
                                                            <Typography variant="body2">
                                                                {folder.name}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    {uploadedBy !== "" && (
                                                        <Typography variant="body2">
                                                            Uploaded by{" "}
                                                            <Box
                                                                component="span"
                                                                sx={{
                                                                    color: "text.primary",
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                {uploadedBy}
                                                            </Box>
                                                        </Typography>
                                                    )}
                                                </Box>

                                                <Divider sx={{ my: 2.5 }} />

                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        flexWrap: "wrap",
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={
                                                            <EventNoteIcon />
                                                        }
                                                        onClick={
                                                            handleEventDialogOpen
                                                        }
                                                        sx={{
                                                            flex: {
                                                                xs: "1 1 calc(50% - 4px)",
                                                                sm: "0 0 auto",
                                                            },
                                                        }}
                                                    >
                                                        Events
                                                    </Button>
                                                    {(userCanUseAnalysis ||
                                                        userCanUsePlaylists) && (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            startIcon={
                                                                <BarChartIcon />
                                                            }
                                                            onClick={
                                                                handleAnalysisButtonPress
                                                            }
                                                            sx={{
                                                                flex: {
                                                                    xs: "1 1 calc(50% - 4px)",
                                                                    sm: "0 0 auto",
                                                                },
                                                            }}
                                                        >
                                                            Analysis
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant={
                                                            isInFavourites
                                                                ? "contained"
                                                                : "outlined"
                                                        }
                                                        size="small"
                                                        startIcon={
                                                            isInFavourites ? (
                                                                <StarIcon />
                                                            ) : (
                                                                <StarBorderIcon />
                                                            )
                                                        }
                                                        onClick={
                                                            isInFavourites
                                                                ? handleRemoveFromFavouritesPress
                                                                : handleAddToFavouritesPress
                                                        }
                                                        sx={{
                                                            flex: {
                                                                xs: "1 1 calc(50% - 4px)",
                                                                sm: "0 0 auto",
                                                            },
                                                        }}
                                                    >
                                                        {isInFavourites
                                                            ? "Favourited"
                                                            : "Favourite"}
                                                    </Button>
                                                    <Tooltip title="More actions">
                                                        <IconButton
                                                            onClick={
                                                                handleMenuOpen
                                                            }
                                                            aria-label="More video actions"
                                                            sx={{
                                                                ml: "auto",
                                                                border: "1px solid",
                                                                borderColor: "divider",
                                                                borderRadius: 2,
                                                            }}
                                                        >
                                                            <MoreHorizIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Box>

                                    <Card
                                        variant="outlined"
                                        sx={{
                                            borderRadius: 3,
                                            borderColor: "divider",
                                            backgroundColor: "background.paper",
                                            boxShadow:
                                                "0 2px 10px rgba(20, 35, 25, 0.045)",
                                            position: {
                                                md: "sticky",
                                            },
                                            top: { md: 80 },
                                            overflow: "hidden",
                                        }}
                                    >
                                        {file.allowComments ? (
                                            <CommentSection
                                                file={file}
                                                videoPlayer={playerRef}
                                                modern
                                            />
                                        ) : (
                                            <Box
                                                sx={{
                                                    minHeight: 260,
                                                    p: 3,
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    textAlign: "center",
                                                }}
                                            >
                                                <ForumOutlinedIcon
                                                    sx={{
                                                        fontSize: 38,
                                                        mb: 1.25,
                                                        color: "text.disabled",
                                                    }}
                                                />
                                                <Typography
                                                    variant="h6"
                                                    sx={{ mb: 0.5 }}
                                                >
                                                    Comments unavailable
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: "text.secondary",
                                                    }}
                                                >
                                                    Comments are disabled for
                                                    this file.
                                                </Typography>
                                            </Box>
                                        )}
                                    </Card>
                                </Box>
                            )}
                        </Container>
                    </Box>
                    <Menu
                        id="menu"
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                    >
                        <MenuItem onClick={handleCopyLinkClick}>
                            <ListItemIcon>
                                <ContentCopyOutlinedIcon fontSize="small" />
                            </ListItemIcon>
                            Copy Link
                        </MenuItem>
                        {userCanSharePublicLink && (
                            <MenuItem
                                onClick={() => {
                                    setAnchorEl(null);
                                    setShareLinkDialogOpen(true);
                                }}
                            >
                                <ListItemIcon>
                                    <PublicOutlinedIcon fontSize="small" />
                                </ListItemIcon>
                                Share Public Link
                            </MenuItem>
                        )}
                        {userIsManager && (
                            <MenuItem onClick={handleEditClick}>
                                <ListItemIcon>
                                    <EditOutlinedIcon fontSize="small" />
                                </ListItemIcon>
                                Edit File
                            </MenuItem>
                        )}
                        {userIsManager && (
                            <MenuItem onClick={handleUsageClick}>
                                <ListItemIcon>
                                    <DataUsageOutlinedIcon fontSize="small" />
                                </ListItemIcon>
                                File Usage Data
                            </MenuItem>
                        )}
                        <MenuItem
                            disabled={!file.allowDownloads}
                            onClick={handleDownloadClick}
                        >
                            <ListItemIcon>
                                <DownloadOutlinedIcon fontSize="small" />
                            </ListItemIcon>
                            Download File
                        </MenuItem>
                        {userIsManager && <Divider />}
                        {userIsManager && (
                            <MenuItem
                                onClick={handleDeleteClick}
                                sx={{ color: "error.main" }}
                            >
                                <ListItemIcon sx={{ color: "error.main" }}>
                                    <DeleteOutlineIcon fontSize="small" />
                                </ListItemIcon>
                                Delete File
                            </MenuItem>
                        )}
                    </Menu>
                </>
            )}
            {confirmDeleteDialogIsOpen && (
                <DeleteFileDialog
                    open={confirmDeleteDialogIsOpen}
                    onClose={handleConfirmDeleteDialogClose}
                    onConfirm={handleConfirmDeletePressed}
                    fileName={file?.displayName}
                    isDeleting={deleteInProgress}
                />
            )}
            {userHasPermission === false && <NoPermissionsMessage />}
            <ToolbarWithLogo />
            {eventDialogOpen && (
                <FileEventsDialog
                    open={eventDialogOpen}
                    onClose={handleCloseEventDialog}
                    file={file}
                />
            )}
            {editDialogOpen && (
                <EditFileDialog
                    open={editDialogOpen}
                    onClose={handleCloseEditDialog}
                    file={file}
                    folder={folder}
                />
            )}
            {analysisSelectDialogOpen && (
                <VideoPlaylistsAndTimelinesDialog
                    open={analysisSelectDialogOpen}
                    onClose={handleCloseAnalysisSelectDialog}
                    video={file}
                    canCreateTimeline={userCanUseAnalysis}
                />
            )}
            {usageDialogOpen && (
                <FileUsageDialog
                    open={usageDialogOpen}
                    onClose={handleCloseUsageDialog}
                    file={file}
                    isVideo
                />
            )}
            {shareLinkDialogOpen && (
                <ShareLinkDialog
                    open={shareLinkDialogOpen}
                    onClose={() => setShareLinkDialogOpen(false)}
                    file={file}
                />
            )}
        </>
    );
}
